/**
 * Utilities for processing pixels of an image.
 */

/**
 * Type for 3 x 3 filters / kernels.
 */
export type Filter3x3Type = [
  [number, number, number],
  [number, number, number],
  [number, number, number],
];

/**
 * Sobel filter for vertical edge detection.
 */
export const sobelFilterVertical: Filter3x3Type = [
  [-1, 0, 1],
  [-2, 0, 2],
  [-1, 0, 1],
];

/**
 * Filter for averaging a pixel.
 */
export const avgFilter: Filter3x3Type = [
  [1, 1, 1],
  [1, 1, 1],
  [1, 1, 1],
];

/**
 * Given a flat array of rgba pixels, returns a flat array of pixels
 * as grayscale, being an average of rgb. The alpha channel is not
 * taken into consideration.
 * @param rgbPixels Array of [r1, g1, b1, a1, r2, g2, b2, a2, ...], every 4 values representing a pixel.
 * @returns         Array of [p1, p2, p3, ...], each value representing a single pixel.
 */
export const rgbaToGrayscale = (rgbPixels: number[]): number[] => {
  const grayscalePixels = [];
  for (let i = 0; i < rgbPixels.length; i += 4) {
    const r = rgbPixels[i];
    const g = rgbPixels[i + 1];
    const b = rgbPixels[i + 2];
    const avg = (r + g + b) / 3;
    grayscalePixels.push(avg);
  }
  return grayscalePixels;
};

/**
 * Returns a pixel value from a pixels array for a given coordinate.
 *
 * @param pixels  Flat array of pixel values.
 * @param width   Width of image.
 * @param x       X coordinate.
 * @param y       Y coordinate.
 * @returns       Pixel value for coordinate. If out of bounds, returns 0.
 */
export const getPixel = (
  pixels: number[],
  width: number,
  x: number,
  y: number,
): number => {
  // Calculate index in pixels array
  const index = y * width + x;

  // Check if out of bounds
  if (index < 0 || index >= pixels.length) return 0;

  return pixels[index];
};

/**
 * Applies a 3x3 filter to a pixel coordinate.
 * @param pixels    Flat array of pixel values.
 * @param filter    3 x 3 filter to apply.
 * @param width     Width of image.
 * @param x         X coordinate.
 * @param y         Y coordinate.
 * @returns         Pixel value at coordinates after applying filter.
 */
export const applyFilterToPixel = (
  pixels: number[],
  filter: Filter3x3Type,
  width: number,
  x: number,
  y: number,
): number => {
  const pixelCoords = [
    [
      [x - 1, y - 1],
      [x, y - 1],
      [x + 1, y - 1],
    ],
    [
      [x - 1, y],
      [x, y],
      [x + 1, y],
    ],
    [
      [x - 1, y + 1],
      [x, y + 1],
      [x + 1, y + 1],
    ],
  ];

  let sum = 0;
  for (let curX = 0; curX < 3; curX++) {
    for (let curY = 0; curY < 3; curY++) {
      // Finder coords of current filter to apply filter to
      const filterX = pixelCoords[curX][curY][0];
      const filterY = pixelCoords[curX][curY][1];

      // Note that out of bounds operations are not directly handled. However,
      // it is handled, as getPixel returns 0 for out of bounds.

      const pixelValue = getPixel(pixels, width, filterX, filterY);
      const value = filter[curX][curY] * pixelValue;
      sum += value;
    }
  }

  const avg = sum / 9;
  return avg;
};

/**
 * Applies a filter to all pixels in a pixel array.
 * @param pixels    Flat array of pixel values.
 * @param filter    3 x 3 filter to apply.
 * @param width     Width of image.
 * @returns         Flat array of pixels processed with filter.
 */
export const applyFilterToPixels = (
  pixels: number[],
  filter: Filter3x3Type,
  width: number,
): number[] => {
  const res = [];
  for (let i = 0; i < pixels.length; i++) {
    const x = i % width;
    const y = Math.floor(i / width);
    if (x === 0 || x === width - 1 || y === 0 || y === width - 1) {
      res.push(0);
    } else {
      res.push(applyFilterToPixel(pixels, filter, width, x, y));
    }
  }
  return res;
};

/**
 * Calculates the sharpness of an image. For best results, first apply vertical
 * sobel filter to pixels, followed by an average filter.
 *
 * The resulting number is the sharpness of the image.
 *
 * At the time of implementation, a threshold of 1 was a fairly good measurement of
 * sharpness. A value below 1 can be used for determining if an image is blurry.
 *
 * Algorithm based on:
 * https://www.quora.com/What-algorithm-can-be-used-to-detect-image-blur-or-sharp
 *
 * @param pixels    Flat array of pixel values.
 * @param width     Image width.
 * @param height    Image height
 * @returns
 */
export const calculateImageSharpness = (
  pixels: number[],
  width: number,
  height: number,
): number => {
  let l1Sum = 0;
  let l2Sum = 0;

  // We start one pixel into the image, as we are operating on the left and top pixels
  // around the pixel currently being processed.
  for (let y = 1; y < height; y++) {
    for (let x = 1; x < width; x++) {
      const curVal = getPixel(pixels, width, x, y);
      const leftVal = getPixel(pixels, width, x - 1, y);
      const topVal = getPixel(pixels, width, x, y - 1);

      l1Sum += Math.abs(curVal - leftVal) + Math.abs(curVal - topVal);
      l2Sum += Math.pow(curVal - leftVal, 2) + Math.pow(curVal - topVal, 2);
    }
  }

  // Original algorithm used the below equation. However, for large images,
  // this resulted in such a low number that it was rounded to 0 by javascript
  // Instead, halfPow has been changed to this.
  // Original formula: Math.pow(0.5, pixels.length);
  const halfPow = 1 / pixels.length;

  const l1 = halfPow * l1Sum;
  const l2 = halfPow * l2Sum;

  const blurriness = (l2 / l1) * l1;

  return blurriness;
};

/**
 * Returns true if an image is blurry. Else returns false.
 *
 * @param pixels                Flat array of image pixels. Should be colour pixels of type [r1,g1,b1,a1,r2,g2,b2,a2,...].
 * @param width                 Width of image.
 * @param height                Height of image.
 * @param sharpnessThreshold    If sharpness value is below threshold, image is considered blurry.
 * @returns                     True if image is blurry, else false.
 */
export const pixelsAreBlurry = (
  rgbaPixels: number[],
  width: number,
  height: number,
  sharpnessThreshold = 1,
): [number, boolean] => {
  // Convert image to grayscale.
  const grayscalePixels = rgbaToGrayscale(rgbaPixels);

  // Apply vertical edge detection to pixels
  const sobelPixels = applyFilterToPixels(
    grayscalePixels,
    sobelFilterVertical,
    width,
  );

  // Smooth found edges
  const smoothPixels = applyFilterToPixels(sobelPixels, avgFilter, width);

  // Calculate sharpness value
  const sharpness = calculateImageSharpness(smoothPixels, width, height);

  // Return true if sharpness is below sharpness threshold
  return [sharpness, sharpness < sharpnessThreshold];
};
